/**
 * JUB-154: event creation wizard state.
 *
 * - `answers` is what the screens and the live preview show; typing updates
 *   it at once (optimistic), the server snapshot catches up behind.
 * - Saves run one request at a time. A save queued while another is in
 *   flight is merged with any queued save for the same screen (same step and
 *   moment), so fast typing never fans out into racing requests.
 * - Text fields autosave 600 ms after the last keystroke (`saveDebounced`).
 * - Every queued save, autosave timer and response is bound to a session:
 *   the signed-in account, the event uuid and a generation counter. Opening
 *   another event, reset() or a logout starts a new session; work of the old
 *   one is cancelled (its waiters reject with OnboardingSessionChanged) and
 *   its late responses are discarded, so an edit can never land on another
 *   event and a stale answer can never replace the current one.
 * - A failed save is kept (merged with later edits of the same screen) and
 *   keeps the status at "error" until retry() succeeds or discardFailed() is
 *   chosen. flush()/complete() refuse while anything is unresolved, so the
 *   wizard never completes over a lost edit. Server answers do not replace
 *   local answers while anything is pending or failed.
 * - The design (JUB-213) is saved through the landing settings, in the same
 *   one-at-a-time slot as the wizard saves, and shown at once; a wizard answer
 *   that arrives meanwhile keeps showing the new design, and a failed design
 *   save goes back to the design the server has.
 */
import { create } from "zustand";
import {
  completeOnboarding,
  getOnboarding,
  onboardingErrorOf,
  patchOnboarding,
  startOnboarding,
} from "@/services/api/onboarding";
import { updateEventLandingSettings } from "@/services/api/eventSettings";
import { useAuthStore } from "@/stores/authStore";
import type { EventTemplateId } from "@/lib/event-templates";
import type {
  CompleteVia,
  MomentAnswer,
  MomentSlot,
  OnboardingAnswers,
  OnboardingPatch,
  OnboardingSnapshot,
  OnboardingType,
} from "@/lib/onboarding/types";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export const AUTOSAVE_DELAY_MS = 600;

/** Rejection for work that belonged to a session that is no longer current. */
export class OnboardingSessionChanged extends Error {
  constructor() {
    super("onboarding_session_changed");
    this.name = "OnboardingSessionChanged";
  }
}

/** flush()/complete() refusal while a save is still failed or pending. */
export class OnboardingUnsavedChanges extends Error {
  constructor() {
    super("onboarding_unsaved_changes");
    this.name = "OnboardingUnsavedChanges";
  }
}

type Waiter = { resolve: (s: OnboardingSnapshot) => void; reject: (e: unknown) => void };
type QueueEntry = { key: string; body: OnboardingPatch; uuid: string; gen: number; waiters: Waiter[] };
type Session = { gen: number; uuid: string | null; account: string | null };

interface OnboardingState {
  snapshot: OnboardingSnapshot | null;
  answers: OnboardingAnswers | null;
  saveStatus: SaveStatus;
  lastError: { code: string | null; errors: Record<string, string[]> } | null;
  /** Screens whose last save failed and still waits for retry/discard. */
  failedKeys: string[];
  startKey: string | null;

  start: (type: OnboardingType, language: "ro" | "en") => Promise<OnboardingSnapshot>;
  load: (uuid: string) => Promise<OnboardingSnapshot>;
  setAnswers: (changes: Partial<OnboardingAnswers>) => void;
  setMoment: (slot: MomentSlot, changes: Partial<MomentAnswer>) => void;
  save: (body: OnboardingPatch) => Promise<OnboardingSnapshot>;
  saveDebounced: (body: OnboardingPatch) => void;
  flush: () => Promise<void>;
  retry: () => Promise<void>;
  discardFailed: () => void;
  complete: (via: CompleteVia) => Promise<OnboardingSnapshot>;
  /** Choose the event's design: shown at once, saved through the landing settings (JUB-213). */
  setTheme: (theme: string) => Promise<void>;
  reset: () => void;
}

/** Same screen ⇔ same step + same moment slot/part. */
export function saveKeyOf(body: OnboardingPatch): string {
  const data = body.data ?? {};
  return [body.step, data.slot ?? "", data.part ?? ""].join(":");
}

/** Merge a later save into an earlier one for the same screen. */
export function mergePatches(earlier: OnboardingPatch, later: OnboardingPatch): OnboardingPatch {
  return {
    ...earlier,
    ...later,
    data: { ...(earlier.data ?? {}), ...(later.data ?? {}) },
    done: Boolean(earlier.done || later.done) || undefined,
    skip: later.skip ?? earlier.skip,
  };
}

const newKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

const currentAccount = (): string | null => useAuthStore.getState().user?.uuid ?? null;

let session: Session = { gen: 0, uuid: null, account: currentAccount() };
let queue: QueueEntry[] = [];
let inFlight: { gen: number } | null = null;
const failed = new Map<string, { body: OnboardingPatch; uuid: string; gen: number }>();
const timers = new Map<string, { timer: ReturnType<typeof setTimeout>; body: OnboardingPatch; uuid: string; gen: number }>();
/** The design chosen last, until the server has it. */
let themeWrite: { theme: string; gen: number } | null = null;
/** The design the server has, as of its last answer. */
let serverTheme: string | null = null;

const isCurrent = (gen: number, uuid?: string | null): boolean =>
  gen === session.gen && session.account === currentAccount() && (uuid === undefined || uuid === session.uuid);

export const useOnboardingStore = create<OnboardingState>((set, get) => {
  const hasPending = () => Boolean(inFlight) || queue.length > 0 || timers.size > 0 || failed.size > 0;
  const failedKeys = () => [...failed.keys()];

  /** Cancel everything of the current session and start a new one. */
  const newSession = (uuid: string | null) => {
    const cancelled = new OnboardingSessionChanged();
    timers.forEach(({ timer }) => clearTimeout(timer));
    timers.clear();
    queue.forEach((entry) => entry.waiters.forEach((w) => w.reject(cancelled)));
    queue = [];
    failed.clear();
    inFlight = null; // a late response of the old session is discarded by its gen check
    themeWrite = null;
    serverTheme = null;
    session = { gen: session.gen + 1, uuid, account: currentAccount() };
  };

  const statusAfterSettle = (): SaveStatus => (failed.size > 0 ? "error" : hasPending() ? "saving" : "saved");

  /** A server snapshot, still showing a design choice that is on its way. */
  const withChosenTheme = (snapshot: OnboardingSnapshot): OnboardingSnapshot => {
    serverTheme = snapshot.theme;
    return themeWrite && themeWrite.gen === session.gen ? { ...snapshot, theme: themeWrite.theme } : snapshot;
  };

  const accept = (snapshot: OnboardingSnapshot) => {
    const shown = withChosenTheme(snapshot);
    set((state) => ({
      snapshot: shown,
      // Never overwrite what is still being typed, saved or waiting for a retry.
      answers: hasPending() && state.answers ? state.answers : snapshot.answers,
    }));
  };

  const pump = async (): Promise<void> => {
    if (inFlight) return;
    const entry = queue.shift();
    if (!entry) return;
    if (!isCurrent(entry.gen, entry.uuid)) {
      entry.waiters.forEach((w) => w.reject(new OnboardingSessionChanged()));
      void pump();
      return;
    }

    const token = { gen: entry.gen };
    inFlight = token;
    set({ saveStatus: "saving" });
    try {
      const snapshot = await patchOnboarding(entry.uuid, entry.body);
      if (inFlight !== token || !isCurrent(entry.gen, entry.uuid)) {
        entry.waiters.forEach((w) => w.reject(new OnboardingSessionChanged()));
        return;
      }
      inFlight = null;
      failed.delete(entry.key);
      accept(snapshot);
      set({ saveStatus: statusAfterSettle(), failedKeys: failedKeys(), lastError: failed.size > 0 ? get().lastError : null });
      entry.waiters.forEach((w) => w.resolve(snapshot));
    } catch (error) {
      if (inFlight !== token || !isCurrent(entry.gen, entry.uuid)) {
        entry.waiters.forEach((w) => w.reject(new OnboardingSessionChanged()));
        return;
      }
      inFlight = null;
      const previous = failed.get(entry.key);
      failed.set(entry.key, {
        body: previous ? mergePatches(previous.body, entry.body) : entry.body,
        uuid: entry.uuid,
        gen: entry.gen,
      });
      set({ saveStatus: "error", lastError: onboardingErrorOf(error), failedKeys: failedKeys() });
      entry.waiters.forEach((w) => w.reject(error));
    } finally {
      if (inFlight === null) void pump();
    }
  };

  const enqueue = (body: OnboardingPatch): Promise<OnboardingSnapshot> => {
    const uuid = session.uuid;
    if (!uuid || session.account !== currentAccount()) return Promise.reject(new OnboardingSessionChanged());
    return new Promise((resolve, reject) => {
      const key = saveKeyOf(body);
      // A failed save of the same screen travels with the new one.
      const stale = failed.get(key);
      const merged = stale ? mergePatches(stale.body, body) : body;
      const queued = queue.find((e) => e.key === key && e.gen === session.gen);
      if (queued) {
        queued.body = mergePatches(queued.body, merged);
        queued.waiters.push({ resolve, reject });
      } else {
        queue.push({ key, body: merged, uuid, gen: session.gen, waiters: [{ resolve, reject }] });
      }
      void pump();
    });
  };

  const waitIdle = async () => {
    while (inFlight || queue.length > 0 || themeWrite) {
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  };

  // Logout or an account switch ends the session: nothing of the previous
  // account may be saved later or shown again.
  useAuthStore.subscribe((auth) => {
    if ((auth.user?.uuid ?? null) !== session.account) {
      newSession(null);
      set({ snapshot: null, answers: null, saveStatus: "idle", lastError: null, failedKeys: [], startKey: null });
    }
  });

  return {
    snapshot: null,
    answers: null,
    saveStatus: "idle",
    lastError: null,
    failedKeys: [],
    startKey: null,

    start: async (type, language) => {
      // One key per wizard attempt: a double tap or a retried request gets
      // the same draft back instead of creating a second event.
      const startKey = get().startKey ?? newKey();
      const gen = session.gen;
      set({ startKey, saveStatus: "saving" });
      try {
        const snapshot = await startOnboarding(type, startKey, language);
        if (!isCurrent(gen)) throw new OnboardingSessionChanged();
        session = { ...session, uuid: snapshot.uuid };
        serverTheme = snapshot.theme;
        set({ snapshot, answers: snapshot.answers, saveStatus: "saved", lastError: null });
        return snapshot;
      } catch (error) {
        if (isCurrent(gen)) set({ saveStatus: "error", lastError: onboardingErrorOf(error) });
        throw error;
      }
    },

    load: async (uuid) => {
      const current = get().snapshot;
      if (current?.uuid === uuid && session.uuid === uuid && get().answers && session.account === currentAccount()) {
        return current;
      }
      newSession(uuid);
      const gen = session.gen;
      set({ snapshot: null, answers: null, saveStatus: "idle", lastError: null, failedKeys: [] });
      const snapshot = await getOnboarding(uuid);
      if (!isCurrent(gen, uuid)) throw new OnboardingSessionChanged();
      serverTheme = snapshot.theme;
      set({ snapshot, answers: snapshot.answers });
      return snapshot;
    },

    setAnswers: (changes) => set((state) => (state.answers ? { answers: { ...state.answers, ...changes } } : {})),

    setMoment: (slot, changes) =>
      set((state) =>
        state.answers
          ? {
              answers: {
                ...state.answers,
                moments: { ...state.answers.moments, [slot]: { ...state.answers.moments[slot], ...changes } },
              },
            }
          : {},
      ),

    save: (body) => {
      // An explicit save supersedes a pending autosave of the same screen.
      const key = saveKeyOf(body);
      const pending = timers.get(key);
      if (pending) {
        clearTimeout(pending.timer);
        timers.delete(key);
        if (pending.gen === session.gen) return enqueue(mergePatches(pending.body, body));
      }
      return enqueue(body);
    },

    saveDebounced: (body) => {
      const uuid = session.uuid;
      if (!uuid) return;
      const key = saveKeyOf(body);
      const pending = timers.get(key);
      if (pending) clearTimeout(pending.timer);
      const merged = pending && pending.gen === session.gen ? mergePatches(pending.body, body) : body;
      const gen = session.gen;
      const timer = setTimeout(() => {
        const own = timers.get(key);
        if (own?.timer === timer) timers.delete(key);
        if (!isCurrent(gen, uuid)) return;
        enqueue(merged).catch(() => undefined); // failures are kept in `failed`
      }, AUTOSAVE_DELAY_MS);
      timers.set(key, { timer, body: merged, uuid, gen });
      set({ saveStatus: "saving" });
    },

    flush: async () => {
      const gen = session.gen;
      const pending = [...timers.entries()].filter(([, t]) => t.gen === gen);
      pending.forEach(([key, { timer }]) => {
        clearTimeout(timer);
        timers.delete(key);
      });
      await Promise.all(pending.map(([, { body }]) => enqueue(body).catch(() => undefined)));
      await waitIdle();
      if (!isCurrent(gen)) throw new OnboardingSessionChanged();
      if (failed.size > 0) {
        set({ saveStatus: "error", failedKeys: failedKeys() });
        throw new OnboardingUnsavedChanges();
      }
    },

    retry: async () => {
      const gen = session.gen;
      const entries = [...failed.entries()].filter(([, f]) => f.gen === gen);
      // Re-queue the failed bodies; enqueue merges the failure in itself.
      await Promise.all(entries.map(([, f]) => enqueue(f.body).catch(() => undefined)));
      await waitIdle();
      if (failed.size > 0) throw new OnboardingUnsavedChanges();
    },

    discardFailed: () => {
      failed.clear();
      const snapshot = get().snapshot;
      set({
        failedKeys: [],
        lastError: null,
        saveStatus: hasPending() ? "saving" : "saved",
        answers: snapshot && !hasPending() ? snapshot.answers : get().answers,
      });
    },

    complete: async (via) => {
      await get().flush(); // refuses while a save is failed: never complete over a lost edit
      const uuid = session.uuid;
      const gen = session.gen;
      if (!uuid) throw new OnboardingSessionChanged();
      try {
        const snapshot = await completeOnboarding(uuid, via);
        if (!isCurrent(gen, uuid)) throw new OnboardingSessionChanged();
        set({ snapshot: withChosenTheme(snapshot), answers: snapshot.answers, saveStatus: "saved", lastError: null });
        return snapshot;
      } catch (error) {
        if (isCurrent(gen, uuid)) set({ saveStatus: "error", lastError: onboardingErrorOf(error) });
        throw error;
      }
    },

    setTheme: async (theme) => {
      const uuid = session.uuid;
      const gen = session.gen;
      if (!uuid || session.account !== currentAccount()) throw new OnboardingSessionChanged();
      const write = { theme, gen };
      themeWrite = write;
      set((state) => (state.snapshot ? { snapshot: { ...state.snapshot, theme } } : {}));

      // One request at a time: let the save in flight finish, then hold the
      // slot, so no wizard save answers with the old design meanwhile.
      while (inFlight || queue.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 20));
        if (!isCurrent(gen, uuid)) throw new OnboardingSessionChanged();
      }
      if (themeWrite !== write) return; // a later choice replaced this one before it was sent

      const token = { gen };
      inFlight = token;
      set({ saveStatus: "saving" });
      try {
        await updateEventLandingSettings(uuid, { theme: theme as EventTemplateId });
        if (!isCurrent(gen, uuid)) throw new OnboardingSessionChanged();
        serverTheme = theme;
      } catch (error) {
        if (!isCurrent(gen, uuid)) throw new OnboardingSessionChanged();
        if (themeWrite === write) {
          const fallback = serverTheme;
          set((state) => (state.snapshot ? { snapshot: { ...state.snapshot, theme: fallback } } : {}));
        }
        throw error;
      } finally {
        if (themeWrite === write) themeWrite = null;
        if (inFlight === token) {
          inFlight = null;
          set({ saveStatus: statusAfterSettle() });
          void pump();
        }
      }
    },

    reset: () => {
      newSession(null);
      set({ snapshot: null, answers: null, saveStatus: "idle", lastError: null, failedKeys: [], startKey: null });
    },
  };
});
